'use client';

import React, { useEffect, useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

function useReveal(ref: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    const els = ref?.current?.querySelectorAll('.rev');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, [ref]);
}

const questions = [
  {
    n: '01',
    q: 'Where are you located?',
    note: 'We are currently only accepting candidates based in Davao, Philippines.',
  },
  {
    n: '02',
    q: 'How many years of SDR or outbound sales experience do you have?',
    note: 'Include any cold calling, prospecting, or lead generation roles.',
  },
  {
    n: '03',
    q: 'What industries or verticals have you sold into?',
    note: 'Be specific — SaaS, logistics, healthcare, financial services, etc.',
  },
  {
    n: '04',
    q: 'Describe your cold calling experience.',
    note: 'How many dials per day? What was your connect-to-meeting rate? What objections did you handle most often?',
  },
  {
    n: '05',
    q: 'What CRM and sales tools have you used?',
    note: 'e.g. Salesforce, HubSpot, Apollo, Outreach, Salesloft, ZoomInfo.',
  },
  {
    n: '06',
    q: 'What US time zone are you available to work?',
    note: 'Our reps work US business hours. Please confirm your availability.',
  },
  {
    n: '07',
    q: 'What is your English fluency level and have you been assessed?',
    note: 'Include any certifications or test scores if available.',
  },
  {
    n: '08',
    q: 'What is your current or most recent monthly compensation?',
    note: 'This helps us understand fit. All figures are kept confidential.',
  },
];

export default function VaApplicationPage() {
  const sec1 = useRef<HTMLDivElement>(null);
  const sec2 = useRef<HTMLDivElement>(null);

  useReveal(sec1);
  useReveal(sec2);

  return (
    <>
      <Header />
      <main>
        {/* Hero */}
        <header
          className="py-24"
          style={{
            background:
              'radial-gradient(900px 500px at 85% -10%, rgba(201,137,65,0.15), transparent 60%), var(--navy-deep)',
          }}
        >
          <div className="max-w-site mx-auto px-7">
            <div className="inline-flex font-mono text-[12px] tracking-[0.18em] uppercase text-primary border border-primary/50 px-4 py-2 rounded-full mb-6">
              Careers · SDR Roles
            </div>
            <h1 className="heading-1 text-white">
              Apply to join<br />CommandVA
            </h1>
            <p className="mt-5 text-[19px] text-muted-cream leading-relaxed max-w-[62ch]">
              We are looking for career SDRs who take cold calling seriously. If you have real outbound experience and you are based in Davao, Philippines, we want to hear from you.
            </p>
            <div className="mt-8 inline-flex items-center gap-3 bg-primary/10 border border-primary/40 rounded-brand px-5 py-3.5">
              <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-primary">Location requirement</span>
              <span className="w-px h-4 bg-primary/30" />
              <span className="text-white text-sm font-semibold">Davao, Philippines only</span>
            </div>
          </div>
        </header>

        {/* How to apply */}
        <section className="py-16 bg-white">
          <div className="max-w-site mx-auto px-7">
            <div className="max-w-[720px]">
              <div className="eyebrow">How to apply</div>
              <h2 className="heading-2 text-ink">Send your application to William</h2>
              <p className="mt-4 text-[17px] text-muted-text leading-relaxed">
                Email{' '}
                <a
                  href="mailto:william@commandva.com"
                  className="text-primary font-semibold hover:text-gold-bright transition-colors"
                >
                  william@commandva.com
                </a>{' '}
                with the subject line <span className="font-mono text-[14px] bg-ink/5 border border-line-color rounded px-2 py-0.5">SDR Application — Davao</span>, answer the questions below in the body of your email, and <strong>attach your resume or CV</strong>.
              </p>
              <div className="mt-6 gold-strip">
                <div>
                  <b className="text-primary">One requirement before anything else:</b> We are currently only hiring candidates based in Davao, Philippines. If you are located elsewhere, please hold your application until we open additional markets.
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Questions */}
        <section ref={sec1} className="py-20 bg-navy">
          <div className="max-w-site mx-auto px-7">
            <div className="rev mb-12 max-w-[680px]">
              <div className="eyebrow">Application questions</div>
              <h2 className="heading-2 text-white">Answer these eight questions</h2>
              <p className="mt-4 text-[17px] text-muted-cream leading-relaxed">
                Copy the questions into your email and answer each one directly. Be specific — vague answers are the fastest way to get passed over.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {questions.map((item, i) => (
                <div
                  key={item.n}
                  className="rev bg-navy-panel border border-line-navy rounded-brand p-6"
                  style={{ transitionDelay: `${i * 0.06}s` }}
                >
                  <b className="font-display text-[40px] text-primary block leading-none font-bold mb-3">
                    {item.n}
                  </b>
                  <p className="text-[16px] text-white font-semibold leading-snug mb-2">
                    {item.q}
                  </p>
                  <p className="text-[13.5px] text-muted-cream leading-relaxed">
                    {item.note}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What we offer */}
        <section ref={sec2} className="py-20">
          <div className="max-w-site mx-auto px-7">
            <div className="rev mb-12 max-w-[680px]">
              <div className="eyebrow">What you get</div>
              <h2 className="heading-2 text-ink">What working at CommandVA looks like</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[
                {
                  tag: 'Dedicated accounts',
                  title: 'One client. Full focus.',
                  body: 'You work one account at a time, full time. No juggling five clients. No split attention. You learn the product, own the outreach, and build real pipeline.',
                },
                {
                  tag: 'US hours',
                  title: 'Real sales hours',
                  body: 'You work US business hours aligned to your client\'s time zone. That means live conversations, real objection handling, and meetings booked in the call.',
                },
                {
                  tag: 'Career path',
                  title: 'This is the job, not a stepping stone',
                  body: 'We hire people who want to be great SDRs, not people waiting to move into something else. If that is you, there is room to grow here.',
                },
                {
                  tag: 'Screening',
                  title: 'Face-to-face interview process',
                  body: 'Every candidate is screened in person in Davao. We test for sales aptitude, English fluency, and coachability before any offer is made.',
                },
              ].map((card, i) => (
                <div
                  key={card.tag}
                  className="rev bg-white border border-line-color rounded-brand p-7"
                  style={{ transitionDelay: `${i * 0.07}s` }}
                >
                  <span className="block font-mono text-[11px] tracking-[0.18em] text-primary uppercase mb-3.5">
                    {card.tag}
                  </span>
                  <h3 className="heading-3 text-ink mb-3">{card.title}</h3>
                  <p className="text-[15px] text-muted-text leading-relaxed">{card.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section
          className="py-28 text-center"
          style={{ background: 'linear-gradient(150deg, var(--navy), var(--navy-deep))' }}
        >
          <div className="max-w-site mx-auto px-7">
            <h2 className="heading-2 text-white">Ready to apply?</h2>
            <p className="mt-5 mb-8 text-muted-cream text-[19px] leading-relaxed max-w-[52ch] mx-auto">
              Email your answers to the eight questions above along with your resume or CV to William. Keep it direct — we read every application.
            </p>
            <a
              href="mailto:william@commandva.com?subject=SDR%20Application%20%E2%80%94%20Davao"
              className="inline-flex items-center gap-2.5 font-semibold text-sm px-7 py-4 rounded-[10px] bg-primary text-navy-deep border border-transparent transition-all duration-200 hover:bg-gold-bright hover:-translate-y-px hover:shadow-gold"
            >
              Email william@commandva.com
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
